import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { DialogService } from 'src/app/services/dialog.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { FieldsService } from 'src/app/services/fields.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { PackagesService } from 'src/app/services/packages.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { StudioTaxMode, StudioTaxService } from 'src/app/services/studio-tax.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { UserService } from 'src/app/services/user.service';
import { UtilityService } from 'src/app/services/utility.service';
import { RouteEventsService } from 'src/app/route-events.service';
import { ViewTableComponent } from '../../base/view-table/view-table.component';

export type ScheduledPaymentStatus = 'overdue' | 'upcoming' | 'unscheduled';

/**
 * One scheduled payment row as returned by the payments_due view REST
 * displays, plus the derived fields this table renders.
 */
export interface ScheduledPaymentDueRow {
  id: string;
  field_payment_date: string;
  field_payment_amount: string;
  field_tax_amount: string;
  field_number_lesson_remaining: string;
  field_amount_paid: string;
  field_paid_in_full: string;
  enrollment_id: string;
  enrollment_title: string;
  status: ScheduledPaymentStatus;
  paid: boolean;
  remaining: number;
  /**
   * Customer-facing figures for studios that add tax on top of prices:
   * the stored amounts are pre-tax, so what the student actually hands over
   * is amount + the row's tax. Null when there is no tax to add.
   */
  withTax: { amount: number; paid: number; remaining: number; tax: number; paidTax: number; remainingTax: number } | null;
}

/**
 * Read-only list of the scheduled payments still outstanding on one enrollment.
 *
 * Uses the same Drupal view that backs the Payments Due / Payments Overdue
 * reports, filtered by enrollment, so staff can check upcoming payment dates
 * without opening the enrollment in edit mode.
 */
@Component({
  selector: 'app-enrollment-payments-due-view',
  templateUrl: './enrollment-payments-due-view.component.html',
  styleUrls: ['./enrollment-payments-due-view.component.css']
})
export class EnrollmentPaymentsDueViewComponent extends ViewTableComponent implements OnInit, OnDestroy {

  @Input() override enrollmentID?: any;

  constructor(
    refreshService: RefreshService,
    dialogService: DialogService,
    entityRESTService: EntityRESTService,
    utilityService: UtilityService,
    fieldsService: FieldsService,
    drupalRESTService: DrupalRESTService,
    taxonomyService: TaxonomyService,
    router: Router,
    activatedRoute: ActivatedRoute,
    authRESTService: AuthRESTService,
    authService: AuthService,
    menuRESTService: MenuRESTService,
    formBuilder: FormBuilder,
    routeEventsService: RouteEventsService,
    permissionsService: PermissionsService,
    renderer2: Renderer2,
    configPages: ConfigPagesService,
    packagesService: PackagesService,
    snackBar: MatSnackBar,
    userRESTService: UserService,
    el: ElementRef,
    changeDetectorRef: ChangeDetectorRef,
    sanitizer: DomSanitizer,
    studioConfigService: StudioConfigService,
    componentRefreshService: ComponentRefreshService,
    translateService: TranslateService,
    private studioTaxService: StudioTaxService
  ) {
    super(
      refreshService,
      dialogService,
      entityRESTService,
      utilityService,
      fieldsService,
      drupalRESTService,
      taxonomyService,
      router,
      activatedRoute,
      authRESTService,
      authService,
      menuRESTService,
      formBuilder,
      routeEventsService,
      permissionsService,
      renderer2,
      configPages,
      packagesService,
      snackBar,
      userRESTService,
      el,
      changeDetectorRef,
      sanitizer,
      studioConfigService,
      componentRefreshService,
      translateService
    );
    // Tax-on-top studios see what the student owes incl. tax, like the
    // enrollment lists; re-annotate if the mode resolves after the rows did.
    this.taxModeSubscription = this.studioTaxService.getTaxMode().subscribe(mode => {
      this.taxMode = mode;
      this.annotateWithTax();
    });
  }

  /** Studio tax mode; null until the studio's tax config has loaded. */
  taxMode: StudioTaxMode | null = null;
  private taxModeSubscription: Subscription;

  // All three are REST displays of the payments_due view. "due" is today or
  // later, "overdue" is before today, and "undated" has no date at all: the
  // by-the-lesson plan dates only its first row and the rest fall due as
  // lessons are used. A scheduled payment appears in exactly one.
  static readonly DUE_ENDPOINT = '/api/v1/reports/enrollments-payments-due';
  static readonly OVERDUE_ENDPOINT = '/api/v1/reports/enrollments-payments-overdue';
  static readonly UNDATED_ENDPOINT = '/api/v1/reports/enrollments-payments-undated';

  // The view's exposed pager only accepts 5, 10, 25 or 50 items per page.
  private static readonly PAGE_SIZE = 50;

  override ngOnDestroy(): void {
    this.taxModeSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  override displayedColumns: string[] = [
    'field_payment_date',
    'status',
    'field_payment_amount',
    'field_amount_paid',
    'remaining',
  ];

  rows: ScheduledPaymentDueRow[] = [];
  totalRemaining = 0;
  overdueCount = 0;
  paidCount = 0;
  loadError = false;

  override getData(params: { parameter: string; value: string; }[]) {
    if (!this.enrollmentID) {
      return;
    }

    this.ShowProgressBar = true;
    this.loadError = false;

    forkJoin({
      overdue: this.fetchAllPages(EnrollmentPaymentsDueViewComponent.OVERDUE_ENDPOINT),
      due: this.fetchAllPages(EnrollmentPaymentsDueViewComponent.DUE_ENDPOINT),
      undated: this.fetchAllPages(EnrollmentPaymentsDueViewComponent.UNDATED_ENDPOINT),
    }).subscribe({
      next: ({ overdue, due, undated }) => {
        const all: ScheduledPaymentDueRow[] = [
          ...overdue.map(row => this.toRow(row, 'overdue')),
          ...due.map(row => this.toRow(row, 'upcoming')),
          ...undated.map(row => this.toRow(row, 'unscheduled')),
        ];

        // Legacy data can leave field_paid_in_full unset on settled payments,
        // so anything already covered by its amount paid is treated as paid
        // and left out of the "due" list.
        const outstanding = all
          .filter(row => !row.paid)
          .sort((a, b) => this.compareRows(a, b));

        this.rows = outstanding;
        this.dataSource = new MatTableDataSource(outstanding);
        this.paidCount = all.length - outstanding.length;
        this.overdueCount = outstanding.filter(row => row.status === 'overdue').length;
        this.annotateWithTax();
        this.ShowProgressBar = false;
      },
      error: (error) => {
        this.loadError = true;
        this.ShowProgressBar = false;
        this.handleError(error);
      },
    });
  }

  /**
   * Attach the incl.-tax figures to each row and total the remaining balance
   * from them. Paid tax is prorated by the share of the row's pre-tax amount
   * that has been paid, the same way the enrollment lists prorate it.
   */
  private annotateWithTax(): void {
    this.rows.forEach(row => {
      const amount = this.toNumber(row.field_payment_amount);
      const tax = this.toNumber(row.field_tax_amount);
      if (!this.taxMode?.hasExclusiveTax || !(tax > 0) || !(amount > 0)) {
        row.withTax = null;
        return;
      }
      const amountPaid = this.toNumber(row.field_amount_paid);
      const paidTax = row.paid ? tax : Math.min(tax, this.round2(tax * (amountPaid / amount)));
      const remainingTax = this.round2(tax - paidTax);
      row.withTax = {
        tax,
        paidTax,
        remainingTax,
        amount: this.round2(amount + tax),
        paid: this.round2(amountPaid + paidTax),
        remaining: this.round2(row.remaining + (row.paid ? 0 : remainingTax)),
      };
    });
    this.totalRemaining = this.rows.reduce((sum, row) => sum + (row.withTax?.remaining ?? row.remaining), 0);
  }

  private round2(value: number): number {
    return Math.round(value * 100) / 100;
  }

  statusLabel(status: ScheduledPaymentStatus): string {
    return status === 'overdue' ? 'Overdue' : 'Upcoming';
  }

  /** Lessons left when an undated (by-the-lesson) payment falls due; null when unknown. */
  lessonsRemaining(row: ScheduledPaymentDueRow): number | null {
    const value = parseInt(String(row.field_number_lesson_remaining ?? ''), 10);
    return isNaN(value) ? null : value;
  }

  // Dated rows first in date order; undated rows after them in the order
  // they fall due, i.e. the most lessons remaining first.
  private compareRows(a: ScheduledPaymentDueRow, b: ScheduledPaymentDueRow): number {
    const aDated = !!a.field_payment_date;
    const bDated = !!b.field_payment_date;
    if (aDated !== bDated) {
      return aDated ? -1 : 1;
    }
    if (aDated) {
      return a.field_payment_date.localeCompare(b.field_payment_date);
    }
    const aLessons = this.lessonsRemaining(a);
    const bLessons = this.lessonsRemaining(b);
    if (aLessons !== null && bLessons !== null && aLessons !== bLessons) {
      return bLessons - aLessons;
    }
    return (parseInt(a.id, 10) || 0) - (parseInt(b.id, 10) || 0);
  }

  /**
   * Fetches every page of one REST display for this enrollment.
   */
  private fetchAllPages(endpoint: string, page = 0): Observable<any[]> {
    const pageSize = EnrollmentPaymentsDueViewComponent.PAGE_SIZE;
    const params = [
      { parameter: 'field_enrollment_target_id', value: String(this.enrollmentID) },
      { parameter: 'items_per_page', value: String(pageSize) },
      { parameter: 'page', value: String(page) },
    ];

    return this._drupalRESTService.httpGET(endpoint, params).pipe(
      switchMap((data: any) => {
        const results: any[] = Array.isArray(data?.results) ? data.results : [];
        const total = Number(data?.count) || 0;
        const hasMore = results.length === pageSize && (page + 1) * pageSize < total;

        if (!hasMore) {
          return of(results);
        }
        return this.fetchAllPages(endpoint, page + 1).pipe(
          map(rest => results.concat(rest))
        );
      })
    );
  }

  private toRow(row: any, status: ScheduledPaymentStatus): ScheduledPaymentDueRow {
    const amount = this.toNumber(row?.field_payment_amount);
    const amountPaid = this.toNumber(row?.field_amount_paid);
    const paid = this.isPaidInFull(row) || (amount > 0 && amountPaid >= amount);
    return {
      ...row,
      status,
      paid,
      remaining: paid ? 0 : Math.max(amount - amountPaid, 0),
      withTax: null,
    };
  }

  private isPaidInFull(row: any): boolean {
    const value = String(row?.field_paid_in_full ?? '').trim().toLowerCase();
    return ['1', 'yes', 'on', 'true'].includes(value);
  }

  private toNumber(value: any): number {
    const parsed = parseFloat(String(value ?? '').replace(/[^0-9.-]/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
}
